import {
  configureSlackAgent,
  connectSlack,
  disconnectSlack,
  getSlackAuthUrl,
  getSlackStatus,
  searchSlackChannels,
  sendSlackTestMessage,
  type ApplicationMessage,
  type SlackAgentConfig,
  type SlackChannel,
  type SlackIntegrationStatus,
} from '@/api';
import { extractErrorDetail } from '@/utils/errorHandler';

export async function requestSlackAuthorizationUrl(): Promise<string> {
  const response = await getSlackAuthUrl();

  if (response.error) {
    throw new Error(extractErrorDetail(response.error, 'Failed to start the Slack connection.'));
  }

  const directUrl = response.data.url;
  if (typeof directUrl === 'string' && directUrl.trim()) {
    return directUrl;
  }

  throw new Error('No authorization URL was returned by the backend.');
}

export async function completeSlackOAuth(code: string): Promise<SlackIntegrationStatus> {
  const response = await connectSlack({
    body: { code },
  });

  if (response.error) {
    throw new Error(extractErrorDetail(response.error, 'Failed to complete the Slack connection.'));
  }

  return response.data;
}

export async function fetchSlackChannels(text = ''): Promise<SlackChannel[]> {
  const response = await searchSlackChannels({
    body: { text, limit: 50 },
  });

  if (response.error) {
    throw new Error(extractErrorDetail(response.error, 'Failed to search Slack channels.'));
  }

  return response.data.results;
}

export async function fetchSlackIntegrationStatus(): Promise<SlackIntegrationStatus> {
  const { data } = await getSlackStatus({ throwOnError: true });
  return data;
}

export async function sendSlackChannelTestMessage(channel_id: string): Promise<ApplicationMessage> {
  const response = await sendSlackTestMessage({
    body: { channel_id },
  });

  if (response.error) {
    throw new Error(extractErrorDetail(response.error, 'Failed to send the Slack test message.'));
  }

  return response.data;
}

export async function removeSlackIntegration(): Promise<SlackIntegrationStatus> {
  const { data } = await disconnectSlack({ throwOnError: true });
  return data;
}

/**
 * Both scopes the agent features need: posting into a channel and the `/gaia-submit` command.
 * A workspace authorized before a scope was added stays `connected` but is missing it, so the
 * fix is re-running the OAuth flow rather than a fresh connection.
 */
export function hasAllSlackScopes(status: SlackIntegrationStatus | null | undefined): boolean {
  return status?.can_push_to_channel === true && status.can_use_commands === true;
}

/** Whether workspace users can pick this agent in `/gaia-submit`. Applies immediately. */
export async function setAgentSlackEnabled(
  intakeAgentUid: string,
  enabled: boolean
): Promise<SlackAgentConfig> {
  const response = await configureSlackAgent({
    path: { intake_agent_uid: intakeAgentUid },
    body: { enabled },
  });

  if (response.error) {
    throw new Error(
      extractErrorDetail(response.error, "Failed to update the agent's Slack setting.")
    );
  }

  return response.data;
}
